import {
  DATETIME_FORMAT,
  DEFAULT_DATE_FORMAT,
  LABEL_DAY_MONTH,
  LABEL_DAY_MONTH_YEAR,
  NO_DATA,
  NO_TIME,
  YM_DATE_FORMAT,
} from "@/constants";
import { TFunction } from "i18next";
import { DateTime, Duration } from "luxon";
import { isEmpty } from "./isEmpty";

/**
 * Converts a value to a luxon DateTime object.
 */
export const toDateTime = (
  value: Date | string | null | undefined,
  format: string = DEFAULT_DATE_FORMAT,
): DateTime | undefined => {
  if (!value) return undefined;
  const dt =
    typeof value === "string"
      ? DateTime.fromFormat(value, format)
      : DateTime.fromJSDate(value);
  return dt.isValid ? dt : undefined;
};

/**
 * Formats a Date object to a string using the specified format.
 * Defaults to DEFAULT_DATE_FORMAT (yyyy-MM-dd) if no format is provided.
 * Returns NO_DATA if the date is invalid.
 */
export const formatDate = (
  date: Date | string | null | undefined,
  format: string = DEFAULT_DATE_FORMAT,
): string => {
  if (!date) return NO_DATA;

  let dt: DateTime;

  if (typeof date === "string") {
    dt = DateTime.fromISO(date);
  } else {
    dt = DateTime.fromJSDate(date);
  }

  if (!dt.isValid) {
    console.error("Invalid DateTime:", {
      date,
      format,
      error: dt.invalidReason,
    });
    return NO_DATA;
  }

  return dt.toFormat(format);
};

/**
 * Formats a date to a string using the RANGE_LABEL_DAY_MONTH format.
 * @param dt - The date to format
 * @returns The formatted date
 */
export const formatDayMonth = (dt: DateTime): string =>
  dt.toFormat(LABEL_DAY_MONTH);

/**
 * Formats a date to a string using the RANGE_LABEL_DAY_MONTH_YEAR format.
 * @param dt - The date to format
 * @returns The formatted date
 */
export const formatlDayMonthYear = (dt: DateTime): string =>
  dt.toFormat(LABEL_DAY_MONTH_YEAR);

/**
 * Formats a period string to a localized period string.
 * Returns "--" if the input is null or invalid.
 */
export const formatPeriod = (period: string | null | undefined): string => {
  if (!period) return NO_DATA;
  return formatDate(period, YM_DATE_FORMAT);
};

/**
 * Formats a time string to HH:MM format.
 * Returns "--:--" if the input is null or invalid.
 */
export const formatTime = (time: string | null): string => {
  if (!time) return NO_TIME;
  return time.substring(0, 5); // HH:MM format
};

/**
 * Formats a datetime string to a localized date and time string.
 * Returns "--" if the input is null or invalid.
 */
export const formatDateTime = (
  datetime: string | null | undefined,
  format: string = DATETIME_FORMAT,
): string => {
  if (!datetime) return NO_DATA;

  const dt = DateTime.fromISO(datetime);
  if (!dt.isValid) return NO_DATA;

  return dt.toFormat(format);
};

/**
 * Formats coordinates to a readable string in "lat / lng" format.
 * Accepts string or number coordinates.
 */
export const formatCoordinates = (
  latitude?: string | number | null,
  longitude?: string | number | null,
): string => {
  if (isEmpty(latitude) && isEmpty(longitude)) {
    return NO_DATA;
  }

  const formatValue = (value?: string | number | null) =>
    isEmpty(value) ? "-" : String(value);

  return `${formatValue(latitude)} / ${formatValue(longitude)}`;
};

/**
 * Formats a duration to a readable hours and minutes format (e.g., "2h 30m").
 * Accepts duration in hours (as string/number, including decimal hours like "16.25").
 * Returns "Not set" if the input is null or invalid.
 */
export const formatDuration = (
  duration: string | number | null | undefined,
  t: TFunction,
): string => {
  // Handle null, undefined, or empty string
  if (duration == null || duration === "") return t("common.notSet");

  // Ensure we have a string
  const durationStr =
    typeof duration === "string" ? duration : String(duration);

  // Parse to number
  const numericDuration = parseFloat(durationStr);
  if (isNaN(numericDuration)) return t("common.notSet");

  // Always treat as hours (database stores durations in hours)
  const dur = Duration.fromObject({ hours: numericDuration });

  const totalMinutes = Math.round(dur.as("minutes"));

  if (totalMinutes === 0) return "0m";

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;

  return `${hours}h ${minutes}m`;
};

/**
 * Calculates duration between start and end datetime.
 * - If no startDateTime: returns "Not set"
 * - If startDateTime but no endDateTime: returns diff between now and startDateTime
 * - If both are set: returns diff between startDateTime and endDateTime
 */
export const calculateRealDuration = (
  startDateTime: string | undefined | null,
  endDateTime: string | undefined | null,
  t: TFunction,
): string => {
  if (!startDateTime) return t("common.notSet");

  try {
    const start = DateTime.fromISO(startDateTime);

    if (!start.isValid) {
      return t("common.notSet");
    }

    // Determine the end point - either endDateTime or current time
    const end = endDateTime ? DateTime.fromISO(endDateTime) : DateTime.now();

    if (!end.isValid) {
      return t("common.notSet");
    }

    const diff = end.diff(start, ["hours", "minutes"]);

    // If negative (future time), return "Not started"
    if (diff.as("milliseconds") < 0) return t("notStarted");

    const totalMinutes = Math.floor(diff.as("minutes"));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0 && minutes === 0) return "0m";
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;

    return `${hours}h ${minutes}m`;
  } catch (error) {
    return t("common.notSet");
  }
};

/**
 * Formats file size in bytes to human-readable format (B, KB, MB).
 */
export const formatFileSize = (sizeBytes: number): string => {
  if (sizeBytes < 1024) {
    return `${sizeBytes} B`;
  } else if (sizeBytes < 1024 * 1024) {
    return `${(sizeBytes / 1024).toFixed(2)} KB`;
  } else {
    return `${(sizeBytes / (1024 * 1024)).toFixed(2)} MB`;
  }
};

/**
 * Formats a boolean value to a translated string ("Tak" / "Nie").
 * Returns empty string if the value is null or undefined.
 * @param value - The boolean value to format
 * @param t - Translation function
 * @param translationKey - Optional custom translation key prefix (default: "booleanValues")
 */
export const formatBoolean = (
  value: boolean | null | undefined,
  t: TFunction,
  translationKey = "booleanValues",
): string => {
  if (value === null || value === undefined) {
    return "";
  }
  return t(`${translationKey}.${value}`);
};

/**
 * Formats days until 26th birthday as an integer.
 * Positive values show without minus, negative values show with minus.
 * @param value - The number of days (can be negative if past 26th birthday)
 */
export const formatDaysUntil26Birthday = (
  value: number | null | undefined,
): string => {
  if (value === null || value === undefined) {
    return "";
  }
  return Math.round(value).toString();
};

/**
 * Display-only: formats values for the UI with `,` as the decimal separator.
 * Stored and sent values (DB, API) use `.`; this maps `.` → `,` for labels, tables, inputs, etc.
 */
export const formatNumber = (
  value?: number | string | null,
  defaultValue = NO_DATA,
) => {
  if (value === null || value === undefined) {
    return defaultValue;
  }
  return value.toString().replace(".", ",");
};

/**
 * Maps user-typed or pasted text to the same decimal convention as DB/API (`.`).
 * Use before `parseFloat` / Zod transforms so strings match persisted number format.
 */
export const formatCommaDecimalToDot = (raw: string): string =>
  raw.replace(/,/g, ".");
